import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";

// Pages
import HomePage from "./pages/HomePage";
import StorePage from "./pages/StorePage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import RepairServicesPage from "./pages/RepairServicesPage";
import PriceListPage from "./pages/PriceListPage";
import BookingPage from "./pages/BookingPage";
import MyBookingsPage from "./pages/MyBookingsPage";
import MyOrdersPage from "./pages/MyOrdersPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AdminDashboard from "./pages/AdminDashboard";
import ComparatorPage from "./pages/ComparatorPage";
import AiAssistant from "./components/AiAssistant";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-pulse text-xl">Caricamento...</div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (adminOnly && user.role !== 'admin') {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Layout with Header and Footer
const MainLayout = () => {
    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1">
                <Outlet />
            </main>
            <Footer />
        </div>
    );
};

// Auth Layout (no header/footer)
const AuthLayout = () => {
    return (
        <div className="min-h-screen bg-background">
            <Outlet />
        </div>
    );
};

// Admin Layout (no footer)
const AdminLayout = () => {
    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1">
                <Outlet />
            </main>
        </div>
    );
};

function AppRoutes() {
    return (
        <Routes>
            {/* Main Layout Routes */}
            <Route element={<MainLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/store" element={<StorePage />} />
                <Route path="/store/:id" element={<ProductDetailPage />} />
                <Route path="/riparazioni" element={
                    <ProtectedRoute>
                        <RepairServicesPage />
                    </ProtectedRoute>
                } />
                <Route path="/listino" element={
                    <ProtectedRoute>
                        <PriceListPage />
                    </ProtectedRoute>
                } />
                <Route path="/confronta" element={<ComparatorPage />} />
                
                {/* Protected Routes */}
                <Route path="/cart" element={
                    <ProtectedRoute>
                        <CartPage />
                    </ProtectedRoute>
                } />
                <Route path="/checkout" element={
                    <ProtectedRoute>
                        <CheckoutPage />
                    </ProtectedRoute>
                } />
                <Route path="/checkout/success" element={
                    <ProtectedRoute>
                        <CheckoutSuccessPage />
                    </ProtectedRoute>
                } />
                <Route path="/checkout/cancel" element={<Navigate to="/cart" replace />} />
                <Route path="/prenota" element={
                    <ProtectedRoute>
                        <BookingPage />
                    </ProtectedRoute>
                } />
                <Route path="/prenotazioni" element={
                    <ProtectedRoute>
                        <MyBookingsPage />
                    </ProtectedRoute>
                } />
                <Route path="/ordini" element={
                    <ProtectedRoute>
                        <MyOrdersPage />
                    </ProtectedRoute>
                } />
            </Route>

            {/* Auth Layout Routes */}
            <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
            </Route>

            {/* Admin Layout Routes */}
            <Route element={<AdminLayout />}>
                <Route path="/admin" element={
                    <ProtectedRoute adminOnly>
                        <AdminDashboard />
                    </ProtectedRoute>
                } />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

const TOAST_OPTIONS = {
    style: {
        background: '#1A2436',
        color: '#FFFFFF',
        border: '1px solid #2E3B52',
    },
};

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <CartProvider>
                    <AppRoutes />
                    <AiAssistant />
                    <Toaster 
                        position="top-right" 
                        toastOptions={TOAST_OPTIONS}
                    />
                </CartProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
